/**
 * Application shell: sidebar, top bar, notification panel, toast host.
 *
 * Replaces the fixed markup that lived at the top of the legacy index.html plus
 * the `.role-doctor` / `.role-admin` / `.role-patient` class-toggling that four
 * separate login paths performed by hand. The router decides what renders; the
 * shell just frames it.
 *
 * The chrome is the legacy chrome: the capsule mark heading the sidebar, the
 * agency mark in the header, colour-coded nav rows, the cyan bar down the
 * active link, and the pulsing refresh button. The rebuild had reduced all of
 * that to one grey column — everything still worked, but the product had lost
 * its face, and a menu of eight identical rows is slower to read than eight
 * coloured ones.
 */

import { Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Icon, Avatar, Spinner } from '../common';
import { useAuth } from '../../context/AuthContext';
import { useData } from '../../context/DataContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePermissions } from '../../context/PermissionContext';
import { LockedFeature } from '../../features/doctor/HospitalRestriction';
import {
  navItemsFor,
  navKey,
  NAV_TINTS,
  NAV_SECTION_LABEL,
  ROLE_LABELS,
  PLATFORM_HOSPITAL_ROWS,
} from './navigation';
import { actingInstitution } from '../../api/client';
import { ROLES } from '../../context/AuthContext';
import { NotificationPanel } from './NotificationPanel';
import { PasswordChangePrompt } from '../../features/settings/ChangePassword';
import { ToastHost } from './ToastHost';
import { HelpAssistant } from '../../features/help/HelpAssistant';
import { useMediaQuery } from '../../hooks/useLocalStorage';
import { logoPlateClass } from '../../domain/logoBackdrop';

/** Hide a logo that fails to load rather than leaving a broken-image box. */
const hideOnError = (event) => {
  event.currentTarget.style.display = 'none';
};

export function AppShell() {
  const { session, role, logout, doctorProfile } = useAuth();
  const { refreshAll, refreshing } = useData();
  const { unread, panelOpen, togglePanel } = useNotifications();
  const { roleTitle, institutionLogo, institutionLogoBackdrop, institutionName } = usePermissions();
  const logoPlate = logoPlateClass(institutionLogoBackdrop);
  const navigate = useNavigate();
  const roleLabel = roleTitle || ROLE_LABELS[role] || '';

  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // The Super Admin working inside a hospital: which one, kept in sync with
  // the header the API client sends (see api/client.js actingInstitution).
  const [acting, setActing] = useState(() => (role === ROLES.ADMIN ? actingInstitution.get() : null));
  useEffect(() => {
    const sync = () => setActing(role === ROLES.ADMIN ? actingInstitution.get() : null);
    sync();
    window.addEventListener('capsule:acting-institution', sync);
    return () => window.removeEventListener('capsule:acting-institution', sync);
  }, [role]);

  const leaveHospital = () => {
    actingInstitution.clear();
    navigate('/admin/institutions');
  };

  const navItems = acting ? [...navItemsFor(role), ...PLATFORM_HOSPITAL_ROWS] : navItemsFor(role);
  const sectionLabel = NAV_SECTION_LABEL[role];
  const displayName = session?.name || 'مستخدم';
  const avatarSrc = doctorProfile?.image || session?.picture || null;

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      {/* Brand */}
      <div className="flex shrink-0 items-center gap-3 border-b border-white/5 p-5">
        <img
          src="/capsule-logo.png"
          alt="كبسولة"
          onError={hideOnError}
          className="brand-mark h-12 w-12 shrink-0 rounded-xl bg-white/5 object-contain p-1"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold text-white">كبسولة</p>
          <p className="truncate text-[11px] text-slate-400">بوابتك للرعاية الصحية</p>
        </div>
        {/* The hospital's own mark, beside the platform's — never instead of
            it. Set from the administrative manager's dashboard. */}
        {institutionLogo && (
          <img
            src={institutionLogo}
            alt={institutionName ? `شعار ${institutionName}` : 'شعار المستشفى'}
            title={institutionName || undefined}
            onError={hideOnError}
            className={`hospital-logo h-12 w-12 shrink-0 rounded-xl object-contain p-1 ${logoPlate}`}
          />
        )}
      </div>

      {/* Navigation */}
      <nav className="custom-scrollbar flex-1 space-y-2 overflow-y-auto p-4">
        {sectionLabel && (
          <p className="px-2 pb-1 text-xs font-semibold text-slate-500">
            {sectionLabel}
          </p>
        )}

        {navItems.map((item) =>
          // A heading row splits one menu into sections — an admin sees the
          // admin tools, then their own patient tabs under a divider.
          item.section ? (
            <p
              key={navKey(item)}
              className="px-2 pb-1 pt-4 text-xs font-semibold text-slate-500"
            >
              {item.section}
            </p>
          ) : (
            <SidebarLink
              key={navKey(item)}
              item={item}
              onNavigate={() => setDrawerOpen(false)}
            />
          ),
        )}
      </nav>

      {/* Who is signed in, and the way out */}
      <div className="shrink-0 border-t border-white/5 p-4">
        <div className="mb-3 flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.04] p-3">
          <Avatar src={avatarSrc} name={displayName} size={40} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{displayName}</p>
            <p className="truncate text-xs text-slate-400">{roleLabel}</p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleLogout}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/[0.08] px-4 py-2.5 text-sm font-medium text-red-300/80 transition-all hover:border-red-500/40 hover:bg-red-500/[0.18] hover:text-red-300"
        >
          <Icon name="log-out" size={16} />
          تسجيل الخروج
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-backdrop flex min-h-screen">
      {/* Desktop sidebar */}
      {isDesktop && (
        <aside
          className="sticky top-0 h-screen w-72 shrink-0 border-l border-white/[0.06]"
          style={{
            background: 'var(--capsule-sidebar)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
          }}
        >
          {sidebar}
        </aside>
      )}

      {/* Mobile drawer */}
      {!isDesktop && drawerOpen && (
        <div className="fixed inset-0 z-[150] flex">
          <div
            className="flex-1 bg-black/70"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <aside className="h-full w-72 border-l border-white/5 bg-slate-950">{sidebar}</aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-[100] flex items-center gap-3 border-b border-white/5 bg-slate-950/80 px-4 py-3 backdrop-blur">
          {!isDesktop && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="rounded-lg p-2 text-slate-300 hover:bg-white/5"
              aria-label="فتح القائمة"
            >
              <Icon name="menu" size={20} />
            </button>
          )}

          {/* The agency mark, as it appeared across every legacy dashboard */}
          <img
            src="/abbas-jr-developments.png"
            alt="ABBAS JR Developments"
            onError={hideOnError}
            className="brand-mark logo-glow-sm hidden h-12 shrink-0 object-contain sm:block"
          />

          {institutionLogo && (
            <img
              src={institutionLogo}
              alt={institutionName ? `شعار ${institutionName}` : 'شعار المستشفى'}
              onError={hideOnError}
              className={`hospital-logo hidden h-11 w-11 shrink-0 rounded-xl object-contain p-1 sm:block ${logoPlate}`}
            />
          )}

          <div className="min-w-0 flex-1 text-left">
            <p className="truncate text-sm font-bold text-white">{displayName}</p>
            <p className="truncate text-[11px] text-slate-400">{roleLabel}</p>
          </div>

          {/* Smart refresh — glows on its own, spins while it works */}
          <button
            type="button"
            onClick={refreshAll}
            disabled={refreshing}
            title="تحديث ذكي — إعادة تحميل جميع البيانات"
            aria-label="تحديث البيانات"
            className="smart-refresh-btn group shrink-0 rounded-xl p-2.5 disabled:cursor-wait"
          >
            <Icon
              name="rotate-cw"
              size={20}
              className={`text-cyan-400 transition-colors group-hover:text-cyan-300 ${
                refreshing ? 'animate-spin' : ''
              }`}
            />
          </button>

          <button
            type="button"
            onClick={togglePanel}
            className="group relative shrink-0 rounded-xl p-2.5 text-slate-400 transition-colors hover:bg-white/5"
            aria-label={`الإشعارات${unread > 0 ? ` (${unread} غير مقروء)` : ''}`}
          >
            <Icon
              name="bell"
              size={20}
              className="transition-colors group-hover:text-cyan-400"
            />
            {unread > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] animate-pulse items-center justify-center rounded-full border-2 border-[color:var(--capsule-bg)] bg-gradient-to-br from-red-500 to-rose-600 px-1 text-[10px] font-black text-onaccent shadow-lg shadow-red-500/40">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          <Avatar src={avatarSrc} name={displayName} size={36} />
        </header>

        {acting && (
          <div className="acting-banner flex flex-wrap items-center gap-3 border-b border-amber-500/30 bg-gradient-to-l from-amber-500/15 via-orange-500/10 to-transparent px-4 py-2.5 text-sm">
            <span className="tile3d__face tone-amber shrink-0" style={{ width: 34, height: 34 }}>
              <Icon name="hospital" size={18} className="tile3d__icon" />
            </span>
            <p className="min-w-0 flex-1 text-amber-100">
              أنت تعمل الآن داخل <b className="text-white">{acting.name || `المنشأة #${acting.id}`}</b> بصلاحيات
              المدير العام — كل ما تفعله يُسجَّل باسمك.
            </p>
            <button type="button" onClick={leaveHospital} className="btn-ghost !py-1.5 text-xs">
              <Icon name="log-out" size={14} /> الخروج من المستشفى
            </button>
          </div>
        )}

        <main className="min-w-0 flex-1 p-4 md:p-6">
          {/* The department desks load as their own chunks (app/router.jsx);
              only the content area waits for one, never the menu around it. */}
          <Suspense fallback={<Spinner label="جارٍ فتح اللوحة" variant="inline" />}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {panelOpen && <NotificationPanel />}
      <PasswordChangePrompt />
      {/* The help assistant and support, on every screen. */}
      <HelpAssistant />
      <ToastHost />
    </div>
  );
}

/**
 * One nav row.
 *
 * Three shapes, all from the legacy sidebar:
 *   - `tint`     the whole row is coloured (the admin menu)
 *   - `icontint` only the glyph is coloured (doctor and patient menus)
 *   - `pulse`    a live dot replaces the glyph (عرض Live)
 */
function SidebarLink({ item, onNavigate }) {
  const tint = item.tint ? NAV_TINTS[item.tint] : null;
  const iconTint = item.icontint ? NAV_TINTS[item.icontint] : null;

  // A row the account lacks the capability for stays in the menu, greyed, and
  // opens the upsell instead of navigating. The requirement is explicit that a
  // hospital doctor must still SEE the private-practice features — removing the
  // row would mean they never learn the product exists. See
  // features/doctor/HospitalRestriction.jsx.
  const { can, loading: permsLoading } = usePermissions();
  const locked = Boolean(item.locks) && !permsLoading && !can(item.locks);

  if (locked) {
    return (
      <LockedFeature feature={item.label}>
        <div
          className={[
            'sidebar-link group flex items-center gap-3 rounded-xl px-4 py-3 text-sm',
            tint ? `tinted ${tint.bg} ${tint.border} ${tint.text} font-bold` : 'text-slate-300',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Icon
            name={item.icon}
            size={20}
            className={tint ? tint.icon : iconTint ? iconTint.icon : 'text-slate-400'}
          />
          <span className="flex-1">{item.label}</span>
        </div>
      </LockedFeature>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          'sidebar-link group flex items-center gap-3 rounded-xl px-4 py-3 text-sm',
          tint ? `tinted ${tint.bg} ${tint.border} ${tint.text} font-bold` : 'text-slate-300',
          isActive ? 'active' : '',
        ]
          .filter(Boolean)
          .join(' ')
      }
    >
      {item.pulse ? (
        <span className="relative flex h-2.5 w-2.5 shrink-0">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500" />
        </span>
      ) : (
        <Icon
          name={item.icon}
          size={20}
          className={
            tint
              ? tint.icon
              : iconTint
                ? iconTint.icon
                : 'text-slate-400 transition-colors group-hover:text-cyan-400'
          }
        />
      )}

      <span
        className={
          tint ? 'flex-1' : 'flex-1 transition-colors group-hover:text-white'
        }
      >
        {item.label}
      </span>
    </NavLink>
  );
}
